import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Target, Heart, ArrowRight, Accessibility, HandHeart, Shield, Globe, Award, Eye, Ear } from "lucide-react";

function Vocational() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const inclusionAreas = [
    {
      icon: Accessibility,
      title: "Disability Inclusion",
      description: "Promoting full participation of people with disabilities in community life and opportunities",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: HandHeart,
      title: "Service Partnerships",
      description: "Connecting individuals with disability services through strategic partner networks",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: Shield,
      title: "Balanced Delivery",
      description: "Ensuring equitable access to services and opportunities for all disability types",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: Globe,
      title: "Community Outreach",
      description: "Extending inclusive programming across Kayunga and neighboring communities",
      color: "from-orange-500 to-red-500"
    }
  ];

  const impact = [
    { icon: Users, metric: "100%", label: "Inclusive Focus", description: "All disability types supported" },
    { icon: Globe, metric: "Multi", label: "Community Reach", description: "Kayunga & neighboring areas" },
    { icon: Award, metric: "Equal", label: "Service Balance", description: "Equitable opportunity access" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50">
      <Header />
      
      {/* Hero Image Section */}
      <div className="relative pt-24 sm:pt-28">
        <img 
          src="img/child.jpg" 
          className="w-full h-64 md:h-96 object-cover"
          alt="Pathway to Ability"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        
        {/* Hero Content Overlay */}
        <div className="absolute inset-0 flex items-center justify-center pt-20 sm:pt-24">
          <div className="text-center text-white px-4">
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Accessibility className="w-4 h-4 mr-2" />
              Disability Inclusion Initiative
            </div>
            <h1 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                Pathway to Ability
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Creating inclusive communities where everyone thrives
            </p>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="mission-content"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['mission-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-8">
              Promoting <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Inclusion</span>
            </h2>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-blue-100">
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-6">
                  The <strong className="text-blue-700">Pathway to Ability</strong> Disability Outreach Program 
                  is a transformative initiative serving Kayunga and neighboring communities, 
                  dedicated to promoting the full inclusion of people with disabilities.
                </p>
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 p-6 rounded-r-2xl">
                  <p className="text-lg text-gray-700 font-medium">
                    We work toward achieving true inclusion by promoting service opportunities 
                    through strategic partnerships and ensuring balanced, equitable service delivery 
                    for all community members.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Inclusion Areas Grid */}
          <div 
            id="inclusion-grid"
            data-animate
            className={`mb-16 transform transition-all duration-1000 delay-300 ${
              isVisible['inclusion-grid'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Approach</span>
              </h3>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {inclusionAreas.map((area, index) => {
                const Icon = area.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    <div className={`w-16 h-16 bg-gradient-to-r ${area.color} rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-800 mb-4 text-center">{area.title}</h4>
                    <p className="text-gray-600 text-center leading-relaxed">{area.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Impact Metrics */}
          <div 
            id="impact-metrics"
            data-animate
            className={`transform transition-all duration-1000 delay-500 ${
              isVisible['impact-metrics'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Inclusive <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Impact</span>
              </h3>
              <p className="text-xl text-gray-600">Creating opportunities for everyone</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {impact.map((outcome, index) => {
                const Icon = outcome.icon;
                return (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-8 text-white text-center transform hover:scale-105 transition-all duration-300"
                  >
                    <Icon className="w-12 h-12 mx-auto mb-4" />
                    <div className="text-4xl font-black mb-2">{outcome.metric}</div>
                    <div className="text-xl font-semibold mb-2">{outcome.label}</div>
                    <div className="text-blue-100">{outcome.description}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="py-16 bg-white/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div 
            id="values"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['values'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-8">
              Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Values</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl p-6">
                <Eye className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-800 mb-3">Visibility</h4>
                <p className="text-gray-600">Making disability services and opportunities clearly accessible to all</p>
              </div>
              
              <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-2xl p-6">
                <HandHeart className="w-12 h-12 text-purple-600 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-800 mb-3">Partnership</h4>
                <p className="text-gray-600">Building strong networks with service providers and community leaders</p>
              </div>
              
              <div className="bg-gradient-to-r from-indigo-100 to-blue-100 rounded-2xl p-6">
                <Award className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-800 mb-3">Equity</h4>
                <p className="text-gray-600">Ensuring balanced access to services regardless of disability type</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <Accessibility className="w-5 h-5 mr-3" />
              Inclusion • Equity • Empowerment
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Build Inclusive Communities
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Join us in creating a world where people with disabilities have equal access to opportunities, 
              services, and community participation. Together, we can build truly inclusive communities 
              that celebrate and support everyone's abilities.
            </p>
            
            <button 
              onClick={() => window.location.href = '/fund-our-work'}
              className="bg-white text-blue-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
            >
              Support Inclusion <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Vocational;